import * as React from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Check, X } from "lucide-react";

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface ColumnFilterPopoverProps {
  title: string;
  options: FilterOption[];
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
  children: React.ReactNode;
  searchPlaceholder?: string;
  onClear?: () => void;
}

export const ColumnFilterPopover: React.FC<ColumnFilterPopoverProps> = ({
  title,
  options,
  selected,
  onSelectionChange,
  children,
  searchPlaceholder = "Pesquisar...",
  onClear
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    return options.filter(option => 
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);

  const allSelected = selected.length > 0 && selected.length === options.length;
  const someSelected = selected.length > 0;

  const toggleValue = (value: string) => {
    if (selected.includes(value)) {
      onSelectionChange(selected.filter(v => v !== value));
    } else {
      onSelectionChange([...selected, value]);
    }
  };

  const selectAll = () => {
    onSelectionChange(filteredOptions.map(option => option.value));
  };

  const clear = () => {
    onSelectionChange([]);
    onClear?.();
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent 
        align="end" 
        side="bottom"
        sideOffset={8}
        className="z-50 w-72 p-0 bg-white border shadow-lg"
      >
        <div className="bg-popover text-popover-foreground rounded-md border shadow-md">
          {/* Header */}
          <div className="px-3 py-2 border-b bg-muted/30">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{title}</span>
              {someSelected && (
                <span className="text-xs text-muted-foreground">({selected.length} selecionado{selected.length > 1 ? 's' : ''})</span>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between px-3 py-2 border-b text-xs">
            <button 
              className="inline-flex items-center gap-1 hover:underline text-primary"
              onClick={allSelected ? clear : selectAll}
            >
              <Check className="h-3 w-3" /> 
              {allSelected ? "Desmarcar todos" : "Selecionar todos"}
            </button>
            <button 
              className="hover:underline text-muted-foreground"
              onClick={clear}
            >
              Limpar
            </button>
          </div>

          {/* Options */}
          <ScrollArea className="h-48">
            <div className="p-2 space-y-1">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center gap-2 text-sm cursor-pointer px-2 py-1.5 rounded hover:bg-muted/50"
                    onClick={() => toggleValue(option.value)}
                  >
                    <Checkbox 
                      checked={selected.includes(option.value)} 
                      onChange={() => {}} // Controlled by parent click
                    />
                    <span className="flex-1 truncate">{option.label}</span>
                    {option.count && (
                      <span className="text-xs text-muted-foreground">({option.count})</span>
                    )}
                    {selected.includes(option.value) && (
                      <button
                        type="button"
                        aria-label={`Remover ${option.label}`}
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          toggleValue(option.value); 
                        }}
                        className="inline-flex h-5 w-5 items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  Nenhum resultado encontrado
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
};